/**
 * Reconnaissance des paiements qui ne concernent pas les centres de vacances.
 *
 * Ces paiements arrivent sur le même compte bancaire mais relèvent d'une autre
 * comptabilité. Les reconnaître à leur forme évite de les laisser encombrer la
 * file de rapprochement manuel, où ils sont majoritaires.
 *
 * ## Rien n'est écrit automatiquement
 *
 * Ce module ne fait que **proposer une catégorie**. Classer un paiement hors
 * périmètre reste une décision comptable : le moteur la suggère, un humain la
 * valide — d'un clic et par lot, mais il la valide. Un motif trop large
 * enterrerait de l'argent au mauvais endroit sans que personne ne le voie.
 *
 * ## Mesuré sur les 22 fichiers de juin-juillet
 *
 * Sur les 45 paiements reçus sans référence exploitable :
 *
 * | Forme | Nombre | Montant |
 * |---|---|---|
 * | `FACT<année>-<n°>` — facturation partenaires | 7 | 18 368,00 € |
 * | `<SITE><n°>-<code>-<AA>-<AA>` — parascolaire et études dirigées | 15 | 1 014,90 € |
 * | Libellé bancaire sans information | 6 | 237,00 € |
 *
 * Les 18 368 € de facturation partenaires changent la lecture des chiffres :
 * ce qui ressemblait à « la moitié de l'argent en attente de rapprochement
 * manuel » est en réalité, pour l'essentiel, de l'argent qui n'appartient pas à
 * la comptabilité de cette application.
 */

export type OutOfScopeCategory =
  | 'partenaires'
  | 'parascolaire_ed'
  | 'parascolaire_ps'
  | 'avant_la_facturation'
  | 'sans_information';

/**
 * Le premier jour où cette application a facturé quelque chose.
 *
 * La toute première facture porte la date du 30 mai 2025 — 84 en mai, toutes au
 * format ancien `CDV-250530-00001` et sans communication structurée, puis 559
 * en juin. **Avant, cette base n'avait rien à encaisser** : ce qui arrivait sur
 * le compte relevait des garderies, du parascolaire, des partenaires, et d'une
 * comptabilité tenue ailleurs.
 *
 * Un paiement antérieur ne peut donc solder aucune facture d'ici. Le laisser en
 * file de rapprochement, c'est demander à quelqu'un de chercher indéfiniment ce
 * qui n'existe pas : 1 735 paiements et 187 453,04 € au 18 septembre 2026.
 *
 * La borne est au 1er mai et non au 30 : elle vient de l'administration, et
 * elle est volontairement prudente d'un mois — les paiements de mai restent
 * dans la file, où quelqu'un peut encore les rattacher aux 84 premières
 * factures.
 */
export const DEBUT_DE_LA_FACTURATION = '2025-05-01';

/**
 * Ce paiement est-il antérieur à toute facture de cette application ?
 *
 * Compare des dates ISO `AAAA-MM-JJ` en texte : à format fixe, l'ordre
 * lexicographique est l'ordre chronologique, et aucun fuseau ne s'en mêle.
 */
export function avantLaFacturation(valueDate: string | null | undefined): boolean {
  return !!valueDate && valueDate < DEBUT_DE_LA_FACTURATION;
}

export interface OutOfScopeMatch {
  category: OutOfScopeCategory;
  /** Ce qui a déclenché la reconnaissance, à afficher pour que l'humain juge. */
  matched: string;
  label: string;
}

interface Motif {
  category: OutOfScopeCategory;
  label: string;
  pattern: RegExp;
}

const MOTIFS: Motif[] = [
  {
    category: 'partenaires',
    label: 'Facturation partenaires',
    // `FACT2026-20`, `FACT 2026-22`. Format annoncé : FACT + année + numéro de
    // facture partenaire. Cette comptabilité est tenue ailleurs.
    pattern: /\bFACT\s*-?\s*\d{4}\s*-\s*\d+/i,
  },
  {
    category: 'parascolaire_ed',
    label: 'Parascolaire ou études dirigées',
    // `CSH865-EDTS-25-26`, `ISA025-T3-25-26`, `ESF140B-EDT3-25-26`, et la même
    // chose écrite avec des espaces. Le signal fiable est l'année scolaire en
    // fin de chaîne, précédée d'un code de site et d'un code d'activité.
    pattern: /\b[A-Z]{2,4}\s?\d{2,4}[A-Z]?\s*[-\s]\s*[A-Z]{1,4}\d{0,2}\s*[-\s]\s*\d{2}\s*[-\s]\s*\d{2}\b/i,
  },
  {
    category: 'parascolaire_ps',
    label: 'Parascolaire (référence PS)',
    /*
      `CVMC116-PSP-24`, `PSD-25`, `PSH 24`, `PSE-25`. Le code tient en trois
      lettres — `PS` pour parascolaire, puis la lettre du site — suivies de
      l'année scolaire.

      Signalé par l'administration le 18 septembre 2026, et mesuré dans la
      foulée : 998 paiements portent l'une de ces formes, dont 520 attendaient
      encore dans la file de rapprochement pour 37 656,33 €.

      Un seul de ces 998 est affecté à une facture de centre de vacances —
      `CVMC116-PSP-24`, 39,00 €, sur `CDV-MC-2501569` : une communication qui
      cite les deux activités. C'est pourquoi ce motif ne fait que PROPOSER,
      comme les autres, et pourquoi la reprise en base n'a touché qu'aux
      paiements ne portant aucune affectation.
    */
    pattern: /\bPS[PEHD]\s*-?\s*2[45]\b/i,
  },
  {
    category: 'sans_information',
    label: 'Libellé bancaire sans information',
    // `Virement instantané en euros` : le libellé que la banque met quand le
    // donneur d'ordre n'a rien écrit. Ce n'est pas hors périmètre en soi, mais
    // la communication ne dit rien : le rapprochement passera par l'IBAN.
    pattern: /^(virement|paiement|transfer|domiciliation)\b/i,
  },
];

/**
 * Reconnaît la forme d'une communication, ou rend `null`.
 *
 * L'ordre compte : les motifs porteurs d'information passent avant le libellé
 * générique, qui est le plus large.
 */
export function classifyOutOfScope(communication: string | null | undefined): OutOfScopeMatch | null {
  const texte = (communication ?? '').trim();
  if (!texte) return null;

  for (const m of MOTIFS) {
    const trouve = texte.match(m.pattern);
    if (trouve) {
      return { category: m.category, matched: trouve[0].trim(), label: m.label };
    }
  }
  return null;
}
